import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, ClipboardCheck, Clock, Paperclip, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { PageHeader } from '../../components/layout/page-header';
import {
  CardDataTable,
  type CardDataTableColumn,
  type CardDataTableRowDef,
} from '../../components/shared/card-data-table';
import { GradientAvatar } from '../../components/shared/gradient-avatar';
import { StatCard } from '../../components/shared/stat-card';
import { FilterPills } from '../../components/ui/filter-pills';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

export const Route = createFileRoute('/inspections/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('inspection:view')) throw redirect({ to: '/dashboard' });
  },
  component: InspectionsPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface InspectionListItem {
  id: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  findings: string | null;
  nextAction: 'PLEDGE_STUDY' | 'APPROVAL_REQUEST' | null;
  assignedAt: string;
  completedAt: string | null;
  createdAt: string;
  assignedInspector: { id: string; name: string; email: string };
  request: {
    id: string;
    referenceNo: string;
    status: string;
    requestType: { name: string; code: string } | null;
    account: { name: string } | null;
  };
  _count: { attachments: number };
}

// ── API fetch ─────────────────────────────────────────────────────────────────

async function fetchInspections(): Promise<InspectionListItem[]> {
  const token = useAuthStore.getState().token;
  const res = await fetch('/api/v1/inspections', {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error('Failed to load inspections');
  return res.json();
}

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  IN_PROGRESS: {
    label: 'In Progress',
    dot: 'bg-amber-400',
    cls: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  COMPLETED: {
    label: 'Completed',
    dot: 'bg-success-500',
    cls: 'border-success-200 bg-success-50 text-success-700',
  },
  PENDING: {
    label: 'Pending',
    dot: 'bg-gray-400',
    cls: 'border-gray-200 bg-gray-50 text-gray-500',
  },
} as const;

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.PENDING;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
        cfg.cls,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

function NextActionBadge({ action }: { action: string | null }) {
  if (!action) return null;
  const label = action === 'PLEDGE_STUDY' ? 'Pledge Study' : 'Approval';
  const cls =
    action === 'PLEDGE_STUDY'
      ? 'border-rose-200 bg-rose-50 text-rose-600'
      : 'border-blue-200 bg-blue-50 text-blue-600';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
        cls,
      )}
    >
      {label}
    </span>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ── Table columns ─────────────────────────────────────────────────────────────

const COLUMNS: CardDataTableColumn[] = [
  { id: 'permit', header: 'Permit Request' },
  { id: 'customer', header: 'Customer' },
  { id: 'inspector', header: 'Inspector' },
  { id: 'status', header: 'Status' },
  { id: 'files', header: 'Files', align: 'center' },
  { id: 'date', header: 'Date' },
];
const GRID = 'minmax(160px, 1.5fr) minmax(140px, 1.2fr) minmax(120px, 1fr) 140px 70px 100px';

const FILTER_PILLS = [
  { value: '__all__', label: 'All' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

function InspectionsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debSearch, setDebSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebSearch(search), 250);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const {
    data: rawData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['inspections'],
    queryFn: fetchInspections,
  });

  const total = rawData.length;
  const inProgress = rawData.filter((i) => i.status === 'IN_PROGRESS').length;
  const completed = rawData.filter((i) => i.status === 'COMPLETED').length;

  const filtered = rawData.filter((item) => {
    if (statusFilter !== '__all__' && item.status !== statusFilter) return false;
    if (debSearch) {
      const q = debSearch.toLowerCase();
      return (
        item.request.referenceNo.toLowerCase().includes(q) ||
        (item.request.account?.name ?? '').toLowerCase().includes(q) ||
        item.assignedInspector.name.toLowerCase().includes(q) ||
        (item.request.requestType?.name ?? '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const rows: CardDataTableRowDef[] = filtered.map((item) => ({
    id: item.id,
    avatar: <GradientAvatar name={item.request.referenceNo} size="md" shape="rounded" />,
    cells: {
      permit: (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-600 transition-colors">
            {item.request.referenceNo}
          </p>
          <div className="mt-1 flex flex-wrap gap-1">
            {item.request.requestType && (
              <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600">
                {item.request.requestType.code}
              </span>
            )}
            {item.nextAction && <NextActionBadge action={item.nextAction} />}
          </div>
        </div>
      ),
      customer: (
        <p className="truncate text-sm text-gray-700">{item.request.account?.name ?? '—'}</p>
      ),
      inspector: (
        <div className="flex items-center gap-2 min-w-0">
          <GradientAvatar name={item.assignedInspector.name} size="xs" shape="circle" />
          <p className="truncate text-sm text-gray-700">{item.assignedInspector.name}</p>
        </div>
      ),
      status: <StatusBadge status={item.status} />,
      files: (
        <div className="flex items-center justify-center gap-1 text-sm text-gray-600">
          <Paperclip size={13} className="text-gray-400" />
          <span className="font-medium">{item._count.attachments}</span>
        </div>
      ),
      date: (
        <div className="text-xs text-gray-500">
          {item.completedAt ? (
            <>
              <p className="text-[10px] font-medium uppercase text-gray-400 mb-0.5">Completed</p>
              <p className="font-medium text-gray-700">{fmtDate(item.completedAt)}</p>
            </>
          ) : (
            <>
              <p className="text-[10px] font-medium uppercase text-gray-400 mb-0.5">Assigned</p>
              <p className="font-medium text-gray-700">{fmtDate(item.assignedAt)}</p>
            </>
          )}
        </div>
      ),
    },
    onClick: () =>
      navigate({ to: '/inspections/$inspectionId', params: { inspectionId: item.id } }),
  }));

  return (
    <AppLayout>
      <PageHeader title="Inspections" description="All permit inspections across the system" />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<ClipboardCheck size={18} />}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          label="Total Inspections"
          value={total}
          loading={isLoading}
        />
        <StatCard
          icon={<Clock size={18} />}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
          label="In Progress"
          value={inProgress}
          loading={isLoading}
        />
        <StatCard
          icon={<CheckCircle2 size={18} />}
          iconBg="bg-success-50"
          iconColor="text-success-500"
          label="Completed"
          value={completed}
          loading={isLoading}
        />
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-56 flex-1">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by permit, customer, or inspector…"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
          />
        </div>
        <FilterPills options={FILTER_PILLS} value={statusFilter} onChange={setStatusFilter} />
        <span className="ml-auto text-xs text-gray-400">
          {filtered.length} of {total} inspection{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <CardDataTable
        gridTemplateColumns={GRID}
        columns={COLUMNS}
        isLoading={isLoading}
        errorContent={
          isError ? (
            <div className="rounded-2xl border border-error-200 bg-error-50 p-8 text-center">
              <p className="text-sm font-medium text-error-600">Failed to load inspections</p>
            </div>
          ) : undefined
        }
        emptyContent={
          <div className="rounded-2xl border border-gray-200 bg-white p-16 text-center shadow-sm">
            <ClipboardCheck size={40} className="mx-auto mb-4 text-gray-200" />
            <p className="text-sm font-semibold text-gray-500">No inspections found</p>
            <p className="mt-1 text-xs text-gray-400">
              {debSearch || statusFilter !== '__all__'
                ? 'Try adjusting your search or filter'
                : 'Inspections will appear here once permits are submitted for inspection'}
            </p>
          </div>
        }
        rows={rows}
      />
    </AppLayout>
  );
}
